import { useTranslation } from 'react-i18next';
import { FlightRoomOption, getFlightRoomOptionsURL } from '@connect-x-app/hybrid-data-access';
import { WorkstationType } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { WorkstationState } from '@connect-x-app/shared-domain-models';
import { Option } from '@connect-x-app/shared-types';
import { COLORS } from '@connect-x-app/shared-ui';
import { formatTime, getNow, parseDate, partTestId, setUTCHoursAndMinutes } from '@connect-x-app/shared-utils';
import { FLIGHTROOM_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, DatePicker, Icon, TimePicker, TypeAhead } from '@connect-x-app/web-ui';
import { mapToOption, mapToOptions } from '@connect-x-app/web-utils';

import { createAutocompleteLoader } from '../../utils/autocompleteLoader';

import './Workstation.scss';

type WorkstationProps = Omit<WorkstationType, 'workstation'>;

export const Workstation = ({
  handleWorkstationChange,
  handleWorkstationClose,
  handleWorkstationDelete,
  handleWorkstationReset,
  handleWorkstationSubmit,
  isWorkstationChanged,
  workstationDraft,
}: WorkstationProps) => {
  const { t } = useTranslation();

  const handleWorkstationValueChange = (value: readonly Option[] | Option | null, name: string) => {
    const convertedValue = Array.isArray(value) ? (value as Array<Option>).map(v => v.value) : ((value as Option)?.value ?? null);
    handleWorkstationChange(convertedValue, name as keyof WorkstationState);
  };

  const datePickerWithAutoTime = (label: string, name: keyof WorkstationState, value: string | null) => {
    const initialDate = value ? parseDate(value) : null;
    const assignTimeValue = (date: Date | null) => {
      const dateForTime = initialDate ?? getNow();
      return date ? setUTCHoursAndMinutes(date, formatTime(dateForTime, true)) : null;
    };
    return (
      <DatePicker
        label={label}
        name={name}
        onChange={(value, name) => handleWorkstationChange(assignTimeValue(value), name as keyof WorkstationState)}
        selectedValue={initialDate}
        testId={FLIGHTROOM_TEST_IDS.workstation.bound(name)}
      />
    );
  };

  const timeInputFromDate = (name: string, selectedValue: string | null, keyName: keyof WorkstationState) => {
    const dateToUse = selectedValue ?? getNow();
    const mapTimePickerValueToDate = (value?: string | null) => {
      return value ? setUTCHoursAndMinutes(dateToUse, value) : null;
    };
    return (
      <TimePicker
        name={name}
        onChange={(value, _name) => handleWorkstationChange(mapTimePickerValueToDate(value?.value), keyName)}
        selectedValue={selectedValue ? mapToOption(formatTime(dateToUse, true)) : null}
        testId={partTestId(FLIGHTROOM_TEST_IDS.workstation.bound(keyName), 'time')}
      />
    );
  };

  return (
    <div className="workstation-container" data-test={FLIGHTROOM_TEST_IDS.workstation.screen}>
      <div className="workstation-title">
        <Text as="h4" color={COLORS.zulu.$08} uppercase weight="bold">
          {t('WORKSTATION.TITLE')}
        </Text>
        <Icon name="closeCross" onClick={handleWorkstationClose} size={2.4} testId={FLIGHTROOM_TEST_IDS.workstation.close} />
      </div>
      <form className="workstation-form">
        <div
          className="workstation-form__block workstation-form__row workstation-form__row--gap"
          data-test={FLIGHTROOM_TEST_IDS.workstation.section('desk')}
        >
          <div className="workstation-form__col workstation-form__col--gap">
            <TypeAhead
              label={t('WORKSTATION.DESK_NUMBER')}
              loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.DESK))}
              name="desks"
              onChange={handleWorkstationValueChange}
              placeholder={t('WORKSTATION.SEARCH')}
              selectedOptions={mapToOptions(workstationDraft.desks)}
              testId={FLIGHTROOM_TEST_IDS.workstation.criteria('desks')}
            />
          </div>
        </div>
        <div className="workstation-form__block" data-test={FLIGHTROOM_TEST_IDS.workstation.section('flightAircraft')}>
          <div className="workstation-form__block--title-container">
            <Icon colorName={COLORS.zulu.$08} name="aviationAircraftTailTemplateOutline" size={2.4} />
            <Text as="h5" className="workstation-form__block--title" color={COLORS.zulu.$08} size="SM" uppercase weight="bold">
              {t('WORKSTATION.FLIGHT_AIRCRAFT')}
            </Text>
          </div>
          <div className="workstation-form__row">
            <div className="workstation-form__col">
              <TypeAhead
                label={t('WORKSTATION.FLIGHT_NUMBER')}
                loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.FLIGHT_NUMBER))}
                name="flightNumbers"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.flightNumbers)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('flightNumbers')}
              />
            </div>
            <div className="workstation-form__col">
              <TypeAhead
                label={t('WORKSTATION.CALL_SIGN')}
                loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.ATC_CALL_SIGN))}
                name="atcCallSigns"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.atcCallSigns)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('atcCallSigns')}
              />
            </div>
          </div>
          <div className="workstation-form__row">
            <div className="workstation-form__col">
              <TypeAhead
                label={t('WORKSTATION.AIRCRAFT_REGISTRATION')}
                loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.AIRCRAFT))}
                name="aircraftRegistrations"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.aircraftRegistrations)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('aircraftRegistrations')}
              />
            </div>
          </div>
        </div>
        <div className="workstation-form__block" data-test={FLIGHTROOM_TEST_IDS.workstation.section('airports')}>
          <div className="workstation-form__block--title-container">
            <Icon colorName={COLORS.zulu.$08} name="aviationTowerTemplateOutline" size={2.4} />
            <Text as="h5" className="workstation-form__block--title" color={COLORS.zulu.$08} size="SM" uppercase weight="bold">
              {t('WORKSTATION.AIRPORTS')}
            </Text>
          </div>
          <div className="workstation-form__row">
            <div className="workstation-form__col">
              <TypeAhead
                label={t('WORKSTATION.DEPARTURE_AIRPORT')}
                loadOptions={createAutocompleteLoader(
                  getFlightRoomOptionsURL(FlightRoomOption.DEPARTURE_AIRPORT_ICAO),
                  getFlightRoomOptionsURL(FlightRoomOption.DEPARTURE_AIRPORT_IATA),
                )}
                name="departureAirports"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.departureAirports)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('departureAirports')}
              />
            </div>
            <div className="workstation-form__col workstation-form__col--small">
              <TypeAhead
                label={t('WORKSTATION.DEPARTURE_GATE')}
                loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.DEPARTURE_GATE))}
                name="departureGates"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.departureGates)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('departureGates')}
              />
            </div>
            <div className="workstation-form__col">
              <TypeAhead
                label={t('WORKSTATION.DESTINATION_AIRPORT')}
                loadOptions={createAutocompleteLoader(
                  getFlightRoomOptionsURL(FlightRoomOption.DESTINATION_AIRPORT_ICAO),
                  getFlightRoomOptionsURL(FlightRoomOption.DESTINATION_AIRPORT_IATA),
                )}
                name="destinationAirports"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.destinationAirports)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('destinationAirports')}
              />
            </div>
            <div className="workstation-form__col workstation-form__col--small">
              <TypeAhead
                label={t('WORKSTATION.DESTINATION_GATE')}
                loadOptions={createAutocompleteLoader(getFlightRoomOptionsURL(FlightRoomOption.DESTINATION_GATE))}
                name="destinationGates"
                onChange={handleWorkstationValueChange}
                placeholder={t('WORKSTATION.SEARCH')}
                selectedOptions={mapToOptions(workstationDraft.destinationGates)}
                testId={FLIGHTROOM_TEST_IDS.workstation.criteria('destinationGates')}
              />
            </div>
          </div>
        </div>
        <div className="workstation-form__block">
          <div className="workstation-form__block--title-container">
            <Icon colorName={COLORS.zulu.$08} name="datetimeClockgenericTemplateOutline" size={2.4} />
            <Text as="h5" className="workstation-form__block--title" color={COLORS.zulu.$08} size="SM" uppercase weight="bold">
              {t('WORKSTATION.DEPARTURE_ARRIVAL')}
            </Text>
          </div>
          <div className="workstation-form__row" data-test={FLIGHTROOM_TEST_IDS.workstation.section('departure')}>
            <div className="workstation-form__col">
              {datePickerWithAutoTime(t('WORKSTATION.DEPARTURE_FROM'), 'departureFrom', workstationDraft.departureFrom)}
            </div>
            <div className="workstation-form__col workstation-form__col--small workstation-form__col--align-end">
              {timeInputFromDate('departureFromTime', workstationDraft.departureFrom, 'departureFrom')}
            </div>
            <div className="workstation-form__col">
              {datePickerWithAutoTime(t('WORKSTATION.DEPARTURE_TO'), 'departureTo', workstationDraft.departureTo)}
            </div>
            <div className="workstation-form__col workstation-form__col--small workstation-form__col--align-end">
              {timeInputFromDate('departureToTime', workstationDraft.departureTo, 'departureTo')}
            </div>
          </div>
          <div className="workstation-form__row" data-test={FLIGHTROOM_TEST_IDS.workstation.section('arrival')}>
            <div className="workstation-form__col">
              {datePickerWithAutoTime(t('WORKSTATION.ARRIVAL_FROM'), 'arrivalFrom', workstationDraft.arrivalFrom)}
            </div>
            <div className="workstation-form__col workstation-form__col--small workstation-form__col--align-end">
              {timeInputFromDate('arrivalFromTime', workstationDraft.arrivalFrom, 'arrivalFrom')}
            </div>
            <div className="workstation-form__col">
              {datePickerWithAutoTime(t('WORKSTATION.ARRIVAL_TO'), 'arrivalTo', workstationDraft.arrivalTo)}
            </div>
            <div className="workstation-form__col workstation-form__col--small workstation-form__col--align-end">
              {timeInputFromDate('arrivalToTime', workstationDraft.arrivalTo, 'arrivalTo')}
            </div>
          </div>
        </div>
      </form>
      <div className="workstation-buttons">
        <Button
          className="delete-button"
          isBorderless
          isOutline
          onClick={handleWorkstationDelete}
          testId={FLIGHTROOM_TEST_IDS.workstation.delete}
          theme="negative"
          type="reset"
        >
          <Icon colorName={COLORS.etna.$06} name="trash" size={2} />
          <span className="filter-button-text">{t('WORKSTATION.DELETE_ALL')}</span>
        </Button>
        <div className="workstation-buttons-right">
          <Button
            isBorderless
            isOutline
            onClick={handleWorkstationReset}
            testId={FLIGHTROOM_TEST_IDS.workstation.reset}
            theme="dark"
            type="reset"
          >
            <Icon colorName={COLORS.zulu.$08} name="actionResetTemplateOutline" size={2} />
            <span className="filter-button-text">{t('WORKSTATION.CLEAR')}</span>
          </Button>
          <Button
            disabled={!isWorkstationChanged}
            onClick={handleWorkstationSubmit}
            testId={FLIGHTROOM_TEST_IDS.workstation.apply}
            theme="primary"
            type="submit"
          >
            {t('WORKSTATION.APPLY')}
          </Button>
        </div>
      </div>
    </div>
  );
};
